import { useEffect, useState } from 'react';
import type { ApiClient, Regelbetrieb as Zahlen } from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

type Fehlschlag = Zahlen['fehlschlaege'][number];
type Tuerereignis = Zahlen['raumbuchschutz'][number];

/**
 * Der Regelbetrieb der letzten Tage — die Abnahmefrage der Stufe 8 als Karte.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE UNERKLÄRTEN FEHLSCHLÄGE DIE EINZIGE ROTE ZAHL SIND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Gesamtplan nimmt die Produktivstellung mit „eine Woche Regelbetrieb ohne
 * unerklärten Fehlschlag" ab. Ein Fehlschlag mit bekannter Ursache — ein
 * belegtes Modell, ein erreichtes Zeitlimit — kostet Arbeit, stellt den
 * Betrieb aber nicht in Frage. Ein Fehlschlag ohne Ursache tut genau das.
 *
 * Deshalb steht hier NICHT die Zahl der Fehlschläge in Rot: Sie ist selten
 * null, und eine Karte, die dauerhaft rot leuchtet, wird nach drei Tagen nicht
 * mehr angesehen.
 */
export function Regelbetrieb({
  api, projektId,
}: {
  api: ApiClient;
  projektId: string;
}) {
  const [zahlen, setZahlen] = useState<Zahlen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.regelbetrieb(projektId, 7)
      .then(setZahlen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  const unerklaert = zahlen?.fehlschlaegeUnerklaert ?? 0;
  const unerklaerte: Fehlschlag[] = (zahlen?.fehlschlaege ?? []).filter((f) => !f.erklaert);

  const laden = () => {
    setFehler(null);
    setZahlen(null);
    api.regelbetrieb(projektId, 7)
      .then(setZahlen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  // Seit v0.52.3 (TR1): verworfene Türaufnahmen. Ein älterer Server liefert
  // die Liste nicht — dann bleibt sie leer statt die Seite zu brechen.
  const tuerereignisse: Tuerereignis[] = zahlen?.tuerbuchschutz ?? [];
  const tuerbremsen = zahlen?.tuerbuchBremsen ?? 0;

  const tuerspalten: Spalte<Tuerereignis>[] = [
    {
      schluessel: 'auftragsnummer', titel: 'Auftrag', immerSichtbar: true,
      wert: (e) => e.auftragsnummer,
      inhalt: (e) => <strong>{e.auftragsnummer}</strong>,
    },
    {
      schluessel: 'zeitpunkt', titel: 'Zeitpunkt', art: 'datum', breite: '150px',
      wert: (e) => e.zeitpunkt,
      inhalt: (e) => new Date(e.zeitpunkt).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      }),
    },
    {
      schluessel: 'code', titel: 'Ereignis', breite: '140px',
      wert: (e) => (e.code === 'TUEREN_BREMSE' ? 'Bremse' : 'nicht Quelle'),
      inhalt: (e) => (e.code === 'TUEREN_BREMSE'
        ? <StatusBadge zustand="warnung" text="Bremse" />
        : <StatusBadge zustand="neutral" text="nicht Quelle" />),
    },
    { schluessel: 'modell', titel: 'Modell', wert: (e) => e.modell },
    { schluessel: 'text', titel: 'Meldung', wert: (e) => e.text },
  ];

  const spalten: Spalte<Fehlschlag>[] = [
    {
      schluessel: 'auftragsnummer', titel: 'Auftrag', immerSichtbar: true,
      wert: (f) => f.auftragsnummer,
      inhalt: (f) => <strong>{f.auftragsnummer}</strong>,
    },
    {
      schluessel: 'zeitpunkt', titel: 'Zeitpunkt', art: 'datum', breite: '150px',
      wert: (f) => f.zeitpunkt,
      inhalt: (f) => new Date(f.zeitpunkt).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      }),
    },
    { schluessel: 'art', titel: 'Art', wert: (f) => f.art },
    { schluessel: 'maschine', titel: 'Maschine', wert: (f) => f.maschine },
    { schluessel: 'ursachentext', titel: 'Ursache laut Protokoll',
      wert: (f) => f.ursachentext },
  ];

  return (
    <ContentSection
      titel="Regelbetrieb, letzte 7 Tage"
      beschreibung={'Die Abnahmefrage der Stufe 8: eine Woche Regelbetrieb ohne '
        + 'unerklärten Fehlschlag.'}
      meta={zahlen
        ? `${new Date(zahlen.von).toLocaleDateString('de-DE')} bis `
          + `${new Date(zahlen.bis).toLocaleDateString('de-DE')} (UTC-Tage) · `
          + `${zahlen.serverfassung}`
        : undefined}
      aktionen={(
        <button type="button" className="m-knopf" disabled={!zahlen}
          onClick={() => api.regelbetriebPdf(projektId, 7)}>
          Bericht als PDF
        </button>
      )}
    >
      {fehler && (
        <ErrorState
          meldung="Die Zahlen des Regelbetriebs konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {!zahlen && !fehler && (
        <LoadingState text="Zahlen des Regelbetriebs werden geladen …" zeilen={3} spalten={4} />
      )}

      {zahlen && (
        <>
          {/* Nur die unerklärten Fehlschläge tragen eine Ampel: Eine Karte, die
              dauerhaft rot leuchtet, wird nach drei Tagen nicht mehr angesehen. */}
          <KpiReihe>
            <KpiCard titel="Aufträge" wert={zahlen.auftraegeGesamt} />
            <KpiCard titel="Erfolgreich" wert={zahlen.erfolgreich} />
            <KpiCard titel="Fehlgeschlagen" wert={zahlen.fehlgeschlagen}
              vergleich={`davon ${zahlen.fehlschlaegeErklaert} erklärt`} />
            <KpiCard titel="Abgebrochen" wert={zahlen.abgebrochen} />
            <KpiCard titel="Wartend" wert={zahlen.wartend} />
            <KpiCard
              titel="Fehlschläge ohne bekannte Ursache" wert={unerklaert}
              zustand={unerklaert > 0 ? 'fehler' : 'gut'}
              zustandText={unerklaert > 0
                ? 'Abnahme Stufe 8 nicht erfüllt' : 'Abnahme Stufe 8 erfüllt'}
            />
            {/* Raumbuchschutz: Die Bremse heißt, die Raumbuchquelle selbst hat
                einen auffälligen Raumbestand geliefert. Verworfene Aufnahmen
                anderer Modelle sind der Regelfall und nur Vergleichszahl. */}
            <KpiCard
              titel="Raumbuch-Bremse" wert={zahlen.raumbuchBremsen}
              vergleich={`${zahlen.raumbuchNichtQuelle} Aufnahme(n) anderer Modelle verworfen`}
              zustand={zahlen.raumbuchBremsen > 0 ? 'warnung' : 'gut'}
              zustandText={zahlen.raumbuchBremsen > 0
                ? 'Raumaufnahme der Quelle prüfen' : 'Raumbuch unauffällig'}
            />
            {/* Türbuchschutz seit v0.52.3: dieselbe Regel für die Türaufnahme. */}
            <KpiCard
              titel="Türbuch-Bremse" wert={tuerbremsen}
              vergleich={`${zahlen.tuerbuchNichtQuelle ?? 0} Aufnahme(n) anderer Modelle verworfen`}
              zustand={tuerbremsen > 0 ? 'warnung' : 'gut'}
              zustandText={tuerbremsen > 0
                ? 'Türaufnahme der Quelle prüfen' : 'Türbuch unauffällig'}
            />
          </KpiReihe>

          <p>
            <StatusBadge
              zustand={unerklaert > 0 ? 'fehler' : 'gut'}
              text={unerklaert > 0
                ? `${unerklaert} Fehlschlag/Fehlschläge ohne bekannte Ursache`
                : 'Kein unerklärter Fehlschlag'}
            />{' '}
            {unerklaert > 0
              ? 'Die Abnahme der Stufe 8 ist für diesen Zeitraum nicht erfüllt.'
              : 'Die Abnahme der Stufe 8 ist für diesen Zeitraum erfüllt.'}
          </p>

          <DataTable
            name="Fehlschläge ohne bekannte Ursache"
            spalten={spalten}
            zeilen={unerklaerte}
            schluessel={(f) => f.auftragsnummer + f.art}
            dicht
            suche={false}
            spaltenwahl={false}
            ausfuhr
            ausfuhrDateiname="regelbetrieb-unerklaerte-fehlschlaege"
            leer={(
              <EmptyState
                klein
                titel="Kein Fehlschlag ohne bekannte Ursache."
                erklaerung={'Ein Fehlschlag mit bekannter Ursache — belegtes Modell, '
                  + 'erreichtes Zeitlimit — kostet Arbeit, stellt den Betrieb aber '
                  + 'nicht in Frage.'}
              />
            )}
          />

          <DataTable
            name="Verworfene Türaufnahmen"
            spalten={tuerspalten}
            zeilen={tuerereignisse}
            schluessel={(e) => e.auftragsnummer + e.zeitpunkt + e.code}
            dicht
            suche={false}
            spaltenwahl={false}
            ausfuhr
            ausfuhrDateiname="regelbetrieb-tuerbuchschutz"
            leer={(
              <EmptyState
                klein
                titel="Keine Türaufnahme verworfen."
                erklaerung={'Ins Türbuch schreibt nur die Türbuchquelle. Eine Aufnahme, die '
                  + 'mehr als 20 % der aktiven Türen entfernen würde, hält die Bremse an.'}
              />
            )}
          />
        </>
      )}
    </ContentSection>
  );
}
